import React, { useEffect, useState } from "react";
import { makeStyles, Dialog, DialogActions, Typography, Button, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

const useStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: 18,
    width: "min(1080px, calc(100vw - 20px))",
    maxWidth: 1080,
    margin: 10,
  },
  head: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    padding: theme.spacing(2, 2, 1, 2.5),
  },
  title: { fontWeight: 800, fontSize: "1.25rem", lineHeight: 1.25 },
  count: { color: theme.palette.text.secondary, fontSize: 13, marginTop: 4 },
  plan: {
    margin: theme.spacing(0, 2.5, 1),
    padding: "8px 12px",
    borderRadius: 12,
    background: theme.palette.type === "dark" ? "rgba(37,99,235,0.16)" : "#eff6ff",
    color: theme.palette.type === "dark" ? "#dbeafe" : "#1e3a8a",
    fontSize: 13,
    fontWeight: 600,
  },
  body: {
    display: "grid",
    gridTemplateColumns: "200px minmax(0, 1.3fr) minmax(260px, 0.9fr)",
    gap: 14,
    padding: theme.spacing(0, 2.5, 1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  rail: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "row",
      overflowX: "auto",
    },
  },
  railItem: {
    textAlign: "left",
    border: `1px solid ${theme.palette.divider}`,
    background: "transparent",
    borderRadius: 12,
    padding: "8px 10px",
    cursor: "pointer",
    color: theme.palette.text.primary,
    font: "inherit",
    fontSize: 13,
    lineHeight: 1.3,
  },
  railOn: {
    borderColor: theme.palette.primary.main,
    background: theme.palette.type === "dark" ? "rgba(37,99,235,0.18)" : "#eff6ff",
    fontWeight: 700,
  },
  frame: {
    position: "relative",
    borderRadius: 14,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === "dark" ? "#111827" : "#f8fafc",
    minHeight: 220,
  },
  shot: {
    display: "block",
    width: "100%",
    height: "auto",
    animation: "$fade .35s ease",
  },
  spot: {
    position: "absolute",
    borderRadius: 10,
    border: "2px solid #2563eb",
    boxShadow: "0 0 0 999px rgba(15, 23, 42, 0.34)",
    pointerEvents: "none",
    transition: "top .35s ease, left .35s ease, width .35s ease, height .35s ease",
    "&::after": {
      content: '""',
      position: "absolute",
      inset: -2,
      borderRadius: 12,
      animation: "$pulse 1.6s ease-out infinite",
    },
  },
  "@keyframes fade": {
    from: { opacity: 0.35 },
    to: { opacity: 1 },
  },
  "@keyframes pulse": {
    "0%": { boxShadow: "0 0 0 0 rgba(37,99,235,0.55)" },
    "100%": { boxShadow: "0 0 0 12px rgba(37,99,235,0)" },
  },
  detail: { minWidth: 0 },
  stepTitle: { fontWeight: 800, fontSize: 16, marginBottom: 6 },
  stepText: { fontSize: 14, lineHeight: 1.5, color: theme.palette.text.secondary, marginBottom: 10 },
  point: {
    width: "100%",
    textAlign: "left",
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    borderRadius: 12,
    padding: "8px 10px",
    marginBottom: 8,
    cursor: "pointer",
    font: "inherit",
    color: theme.palette.text.primary,
    boxSizing: "border-box",
  },
  pointOn: {
    borderColor: theme.palette.primary.main,
  },
  pointTitle: { fontWeight: 700, fontSize: 13, display: "flex", gap: 8, alignItems: "center" },
  badge: {
    fontSize: 10,
    fontWeight: 800,
    borderRadius: 999,
    padding: "1px 6px",
    background: "#fef3c7",
    color: "#92400e",
  },
  pointText: { fontSize: 13, lineHeight: 1.45, marginTop: 4, color: theme.palette.text.secondary },
  link: { marginTop: 6, padding: 0, minWidth: 0, textTransform: "none", fontWeight: 700 },
}));

const GuideWalk = ({ open, title, planHint, steps, startId, onClose, onOpen, onNavigate, openLabel, closeLabel, backLabel, nextLabel }) => {
  const classes = useStyles();
  const items = Array.isArray(steps) ? steps : [];
  const [index, setIndex] = useState(0);
  const [point, setPoint] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!open) return;
    const found = items.findIndex((step) => step.id === startId);
    setIndex(found >= 0 ? found : 0);
    setPoint(0);
    setPaused(false);
  }, [open, startId, items.length]);

  const step = items[index] || null;
  const points = step?.points || [];
  const active = points[point] || null;

  useEffect(() => {
    if (!open || paused || points.length < 2) return undefined;
    const timer = setInterval(() => {
      setPoint((current) => (current + 1) % points.length);
    }, 4200);
    return () => clearInterval(timer);
  }, [open, paused, index, points.length]);

  const go = (next) => {
    const value = Math.max(0, Math.min(items.length - 1, next));
    setIndex(value);
    setPoint(0);
    setPaused(false);
  };

  const spot = active?.spot;
  return (
    <Dialog open={!!open && !!step} onClose={onClose} scroll="paper" classes={{ paper: classes.paper }} aria-labelledby="guide-walk-title">
      <div className={classes.head}>
        <div>
          <Typography id="guide-walk-title" className={classes.title}>{title}</Typography>
          <div className={classes.count}>{index + 1} / {items.length}</div>
        </div>
        <IconButton aria-label={closeLabel || "Cerrar"} onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </div>
      {planHint ? <div className={classes.plan}>{planHint}</div> : null}
      <div className={classes.body}>
        <div className={classes.rail}>
          {items.map((item, itemIndex) => (
            <button
              key={item.id}
              type="button"
              className={`${classes.railItem} ${itemIndex === index ? classes.railOn : ""}`}
              onClick={() => go(itemIndex)}
            >
              {itemIndex + 1}. {item.nav}
            </button>
          ))}
        </div>
        <div className={classes.frame}>
          <img key={step.image} className={classes.shot} src={step.image} alt="" />
          {spot ? <div className={classes.spot} style={spot} /> : null}
        </div>
        <div className={classes.detail}>
          <div className={classes.stepTitle}>{step.title}</div>
          <div className={classes.stepText}>{step.text}</div>
          {points.map((item, itemIndex) => (
            <div
              key={item.title}
              role="button"
              tabIndex={0}
              className={`${classes.point} ${itemIndex === point ? classes.pointOn : ""}`}
              onClick={() => { setPoint(itemIndex); setPaused(true); }}
              onKeyDown={(event) => { if (event.key === "Enter") { setPoint(itemIndex); setPaused(true); } }}
            >
              <div className={classes.pointTitle}>
                {item.title}
                {item.required ? <span className={classes.badge}>{item.required}</span> : null}
              </div>
              {itemIndex === point ? <div className={classes.pointText}>{item.text}</div> : null}
              {itemIndex === point && item.to ? (
                <Button
                  className={classes.link}
                  color="primary"
                  size="small"
                  endIcon={<OpenInNewIcon />}
                  onClick={(event) => { event.stopPropagation(); onNavigate?.(item.to); }}
                >
                  {item.toLabel}
                </Button>
              ) : null}
            </div>
          ))}
        </div>
      </div>
      <DialogActions>
        <Button onClick={onClose}>{closeLabel || "Cerrar"}</Button>
        <Button startIcon={<ChevronLeftIcon />} disabled={index === 0} onClick={() => go(index - 1)}>
          {backLabel || "Anterior"}
        </Button>
        {index < items.length - 1 ? (
          <Button color="primary" variant="contained" endIcon={<ChevronRightIcon />} onClick={() => go(index + 1)}>
            {nextLabel || "Siguiente"}
          </Button>
        ) : onOpen ? (
          <Button color="primary" variant="contained" startIcon={<OpenInNewIcon />} onClick={onOpen}>
            {openLabel || "Abrir"}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default GuideWalk;
